import { useState, useEffect } from "react";
import { cn } from "@/components/ui/cn";

interface EventReactionButtonProps {
  isSelected: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
  disabled?: boolean;
}

/**
 * Animated reaction button for hang events.
 * Matches mobile EventReactionButton: rotates -45deg on select with spring,
 * scale bounce on press, and tint color change.
 */
export function EventReactionButton({
  isSelected,
  onActivate,
  onDeactivate,
  disabled = false,
}: EventReactionButtonProps) {
  const [pressing, setPressing] = useState(false);

  const rotation = isSelected ? -45 : 0;
  const scale = pressing ? 0.9 : 1;

  const handleClick = () => {
    if (disabled) return;
    setPressing(true);

    if (isSelected) {
      onDeactivate?.();
    } else {
      onActivate?.();
    }
  };

  useEffect(() => {
    if (pressing) {
      const timer = setTimeout(() => setPressing(false), 150);
      return () => clearTimeout(timer);
    }
  }, [pressing]);

  // Use the PNG as a CSS mask so we can color it with any solid color.
  // Deselected: text-secondary (dark gray). Selected: coreColors.blue (#1F4AB3).
  const tintColor = isSelected ? "#1F4AB3" : "var(--color-text-secondary)";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "relative w-10 h-10 rounded-[8px] flex items-center justify-center overflow-hidden",
        "transition-colors duration-300",
        isSelected ? "bg-transparent" : "bg-surface-1",
        disabled && "opacity-50 cursor-not-allowed",
        !disabled && "cursor-pointer"
      )}
    >
      <div
        style={{
          width: 29,
          height: 25,
          backgroundColor: tintColor,
          WebkitMaskImage: "url(/images/reaction_handsUp.png)",
          WebkitMaskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskImage: "url(/images/reaction_handsUp.png)",
          maskSize: "contain",
          maskRepeat: "no-repeat",
          maskPosition: "center",
          transform: `rotate(${rotation}deg) scale(${scale})`,
          transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease",
        }}
      />
    </button>
  );
}
